<template>
  <InputText
    :value="displayValue"
    @input="onInput"
    @blur="onBlur"
    @keydown="onKeyDown"
    placeholder="e.g. 7:30am or 9a-5p"
    class="p-inputtext p-component w-full"
  />
</template>

<script setup>
import { ref, watch, computed } from "vue";
import { parse, isValid, format } from "date-fns";

const props = defineProps({
  modelValue: Date,
  referenceDate: {
    type: Date,
    default: () => new Date(),
  },
});

const emit = defineEmits([
  "update:modelValue",
  "range-detected",
  "enter-pressed",
]);

const rawInput = ref("");
const displayValue = computed(() => {
  if (props.modelValue) {
    return format(props.modelValue, "hh:mm a");
  }
  return rawInput.value;
});

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal && isValid(newVal)) {
      rawInput.value = format(newVal, "hh:mm a");
    }
  },
  { immediate: true }
);

function parseTimeString(input, refDate) {
  // Normalize shorthand like '7a' to '7 a' so format strings match properly
  input = input.trim().replace(/^([0-9]{1,2})([ap])$/i, "$1 $2");

  const hasAmPm = /\b[ap]\.?m?\b/i.test(input);

  const formats = [
    "h:mm a",
    "h:mma",
    "h a",
    "ha",
    "HH:mm",
    "H:mm",
    "hmm a",
    "hmm",
    "h",
  ];

  for (const formatStr of formats) {
    const parsed = parse(input.trim(), formatStr, refDate);
    if (isValid(parsed)) {
      if (!hasAmPm) {
        const hour = parsed.getHours();
        const adjustedHour = inferAmPm(hour);
        if (adjustedHour !== hour) {
          const newDate = new Date(parsed);
          newDate.setHours(adjustedHour);
          return newDate;
        }
      }
      return parsed;
    }
  }

  const numericMatch = input.match(/^(\d{1,2})(?::(\d{1,2}))?$/);
  if (numericMatch) {
    let hour = parseInt(numericMatch[1]);
    const minute = numericMatch[2] ? parseInt(numericMatch[2]) : 0;
    hour = inferAmPm(hour);
    let inferredDate = new Date(refDate);
    inferredDate.setHours(hour, minute, 0, 0);
    return inferredDate;
  }

  return null;
}

function inferAmPm(hour) {
  if (hour >= 1 && hour <= 7) return hour + 12;
  if (hour >= 8 && hour <= 11) return hour;
  if (hour === 12) return 12;
  if (hour === 0) return 12;
  if (hour >= 13 && hour <= 23) return hour;
  return hour;
}

function parseTimeRange(input, refDate) {
  const rangeRegex = /(.+?)(?:\s*[-–—to]\s*)(.+)/i;
  const match = input.match(rangeRegex);

  if (!match) return { startTime: null, endTime: null, isRange: false };

  const startInput = match[1].trim();
  const endInput = match[2].trim();

  // Check if times have explicit AM/PM
  const startHasAmPm = /[ap]\.?m?/i.test(startInput);
  const endHasAmPm = /[ap]\.?m?/i.test(endInput);

  const startTime = parseTimeString(startInput, refDate);
  const endTime = parseTimeString(endInput, refDate);

  if (!startTime || !endTime) {
    return { startTime, endTime, isRange: !!startTime && !!endTime };
  }

  // If end is before or equal to start and neither has explicit AM/PM,
  // assume AM→PM pattern (shifts don't span midnight)
  if (endTime <= startTime && !startHasAmPm && !endHasAmPm) {
    const startHours = startTime.getHours();
    const endHours = endTime.getHours();

    let adjustedStart = new Date(startTime);
    let adjustedEnd = new Date(endTime);

    // If start was inferred as PM, try AM instead
    if (startHours >= 12) {
      adjustedStart.setHours(startHours - 12);
    }

    // If end was inferred as AM, try PM instead
    if (endHours < 12) {
      adjustedEnd.setHours(endHours + 12);
    }

    // Only use adjustment if it creates a valid range (end > start)
    if (adjustedEnd > adjustedStart) {
      return { startTime: adjustedStart, endTime: adjustedEnd, isRange: true };
    }
  }

  return {
    startTime,
    endTime,
    isRange: true,
  };
}

function onInput(e) {
  rawInput.value = e.target.value;
}

function onBlur() {
  if (!rawInput.value.trim()) {
    emit("update:modelValue", null);
    return;
  }

  const { startTime, endTime, isRange } = parseTimeRange(
    rawInput.value,
    props.referenceDate
  );

  if (isRange) {
    emit("update:modelValue", startTime);
    emit("range-detected", endTime);
    rawInput.value = format(startTime, "hh:mm a");
    return;
  }

  const parsed = parseTimeString(rawInput.value, props.referenceDate);
  if (parsed) {
    emit("update:modelValue", parsed);
    rawInput.value = format(parsed, "hh:mm a");
  } else {
    rawInput.value = "";
    emit("update:modelValue", null);
  }
}

function onKeyDown(e) {
  if (e.key === "Enter") {
    e.preventDefault();

    if (!rawInput.value.trim()) {
      emit("update:modelValue", null);
      emit("enter-pressed");
      return;
    }

    const { startTime, endTime, isRange } = parseTimeRange(
      rawInput.value,
      props.referenceDate
    );

    if (isRange) {
      emit("update:modelValue", startTime);
      emit("range-detected", endTime);
      rawInput.value = format(startTime, "hh:mm a");
    } else {
      const parsed = parseTimeString(rawInput.value, props.referenceDate);
      if (parsed) {
        emit("update:modelValue", parsed);
        rawInput.value = format(parsed, "hh:mm a");
      }
    }

    emit("enter-pressed");
  }
}
</script>
